<template>
    <div class="help">
        <div class="help-container">
            <div style="color: #777; float: right; font-size: 12px;">PsFlex - {{ dateCompiled }} - Gabriel Cavecedo</div>
            <h1>Referencia del Lenguaje</h1>
            <p><strong>PsFlex</strong> es un compilador flexible de pseudocódigo. El lenguaje tiene una estructura sencilla y está orientado a la enseñanza. </p>
            <p>A continuación, se presenta una breve guía sobre cada una de las construcciones del lenguaje.</p>
            <h2>Algoritmo</h2>
            <p>Todos los algoritmos o programas escritos en PsFlex comienzan con la palabra clave Algoritmo, seguido de el nombre del algoritmo. Luego, se finaliza con la palabra clave FinAlgoritmo. Por ejemplo, el siguiente algoritmo se llama &quot;Primos&quot;:</p>
            <codemirror class="code-help" v-model="listing[11]" :options="cmOption"></codemirror>
            <h2>Tipos Básicos</h2>
            <p>El lenguaje provee los siguientes tipos básicos:
                <ul class="code-list">
                    <li><span class="code-keyword">Entero</span></li>
                    <li><span class="code-keyword">Real</span></li>
                    <li><span class="code-keyword">Logico</span></li>
                    <li><span class="code-keyword">Caracter</span></li>
                </ul>
            </p>
            <h2>Declaración de Variables</h2>
            <p>La definición de variables simples se hace de la siguiente forma:</p>
            <codemirror class="code-help" v-model="listing[0]" :options="cmOption"></codemirror>
            <p>Además, se pueden definir arreglos de un tamaño T, donde T debe ser una constante entera. Ejemplo:</p>
            <codemirror class="code-help" v-model="listing[10]" :options="cmOption"></codemirror>
            <h2>Asignación</h2>
            <p>La asignación se realiza mediante el operador <- (signo menor seguido de guión medio).</p>
            <p>En el caso de los arreglos, se acceden utilizando corchetes y los índices van de 0 hasta T - 1. Ejemplo:</p>
            <codemirror class="code-help" v-model="listing[1]" :options="cmOption"></codemirror>
            <h2>Comentarios</h2>
            <p>Se pueden incluir comentarios de una línea con el símbolo # ó con //. Se ignora todo hasta llegar al fin de la línea.</p>
            <h2>Si - SiNo</h2>
            <p>El condicional Si, tiene la siguiente sintaxis:</p>
            <codemirror class="code-help" v-model="listing[2]" :options="cmOption"></codemirror>
            <h2>Seg&uacute;n</h2>
            <p>El condicional Seg&uacute;n, tiene la siguiente sintaxis:</p>
            <codemirror class="code-help" v-model="listing[12]" :options="cmOption"></codemirror>
            <h2>Mientras</h2>
            <p>Repite una serie de sentencias mientras la condición se cumpla. Formato:</p>
            <codemirror class="code-help" v-model="listing[3]" :options="cmOption"></codemirror>
            <h2>Repetir</h2>
            <p>Similar a Mientras, pero ejecuta al menos una vez las sentencias. Formato:</p>
            <codemirror class="code-help" v-model="listing[4]" :options="cmOption"></codemirror>
            <h2>Para</h2>
            <p>Ejecuta sentencias un determinado numero de veces. Ejemplo:</p>
            <codemirror class="code-help" v-model="listing[5]" :options="cmOption"></codemirror>
            <h2>Entrada / Salida</h2>
            <p>Se permite escribir y leer desde la pantalla mediante Escribir y Leer. También puede escribir "Sin Saltar" para evitar saltar de línea luego de escribir. Ejemplos: </p>
            <codemirror class="code-help" v-model="listing[6]" :options="cmOption"></codemirror>
            <h2>Esperar</h2>
            <p>La sentencia <b>Esperar <i>num</i>;</b> permite esperar una cantidad determinada de segundos. Donde <i>num</i> es una expresión de tipo Entero.</p>
            <p>También se puede utilizar <b>Esperar Tecla;</b>, que espera a que el usuario presione una tecla antes de continuar.</p>
            <h2>Borrar Pantalla</h2>
            <p>La sentencia <b>Borrar Pantalla;</b> limpia todo lo escrito en pantalla hasta el momento.</p>
            <h2>Operadores</h2>
            <p>Operadores aritméticos básicos: Suma (+), Resta (-), Multiplicación (*), División (/) y Potencia (^).</p>
            <p>Operadores lógicos: Conjunción (&), Disyunción (|), Negación (~).</p>
            <p>Operadores de comparación: Igual (=), Mayor (>), Menor (<), Mayor o Igual (>=), Menor o Igual (<=) y Distinto (<>).</p>
            <h2>Funciones Primitivas</h2>
            <p>Raíz Cuadrada (RC), Resto o Módulo (mod) y Valor Absoluto (ABS):</p>
            <codemirror class="code-help" v-model="listing[7]" :options="cmOption"></codemirror>
            <h2>Procedimientos</h2>
            <p>Se permiten definir procedimientos para agrupar sentencias. Los procedimientos pueden tener cero o más parámetros.</p>
            <p>Los parámetros pueden ser pasados por valor o por referencia.</p>
            <p>Para pasar un arreglo, se debe indicar utilizando "[]" luego del nombre del parámetro.</p>
            <p>Si no se indica el tipo de pasaje, se asume que es por valor. También se pueden agrupar varios parámetros en una misma definición.</p>
            <p>Los procedimientos y las funciones deben declararse antes de su uso.</p>
            <codemirror class="code-help" v-model="listing[8]" :options="cmOption"></codemirror>
            <h2>Funciones</h2>
            <p>Las funciones son similares a los procedimientos pero devuelven un valor y por lo tanto se pueden utilizar en expresiones.</p>
            <p>Tener en cuenta que las funciones no pueden recibir parámetros por referencia.</p>
            <p>Los procedimientos y las funciones deben declararse antes de su uso.</p>
            <codemirror class="code-help" v-model="listing[9]" :options="cmOption"></codemirror>
        </div>
    </div>
</template>
<script>
    import { codemirror } from 'vue-codemirror'
    import 'codemirror/lib/codemirror.css'
    import 'codemirror/addon/display/autorefresh.js'
    import 'codemirror/addon/lint/lint.css'
    import 'codemirror/addon/mode/simple.js'
    import 'codemirror/addon/lint/lint.js'

    import './../../Codemirror/mode'
    import './../../Codemirror/lint'

    let listing = []

    listing[listing.length] = `
Definir numero Como Entero;
Definir precio Como Real;
Definir letra Como Caracter;
Definir encendido Como Logico;`.trim()

    listing[listing.length] = `
Definir numero[10] Como Entero;
numero[0] <- 11;`.trim()

    listing[listing.length] = `
Si condicion Entonces
    // Se ejecuta si condición es Verdadero
SiNo
    // Se ejecuta si condición es Falso
FinSi`.trim()

    listing[listing.length] = `
Mientras condicion Hacer
    // Se ejecuta siempre que condición es Verdadero
    // Sentencias...
    // Vuelve al comienzo del Mientras y evalúa la condición nuevamente
FinMientras`.trim()

    listing[listing.length] = `
Repetir
    // Se ejecuta al menos una vez
    // Sentencias...
    // Evalua la condición y si es verdadera termina, sino, repite.
Hasta Que condicion`.trim()

    listing[listing.length] = `
Para i <- 1 Hasta 10 Con Paso 2 Hacer
    // Sentencias...
    // Suma 2 a i y si es menor o igual que 10 repite
FinPara`.trim()

    listing[listing.length] = `
Definir i, a Como Entero;

Escribir "Hola", " mundo.";
Escribir "10 por 2 es igual a ", 10 * 2;
Escribir "Ingrese dos números enteros:";
Leer i, a;
Escribir Sin Saltar "El monto es: ";
Escribir a;
`.trim()

    listing[listing.length] = `
Definir i Como Real;
Definir e Como Entero;
i <- RC(16);
i <- ABS(-12.2);
e <- mod(4, 2);
`.trim()

    listing[listing.length] = `
Procedimiento calcularTotal(resultado Como Real Por Referencia, monto Como Real Por Valor)
    Definir gastosFijos Como Real;
    gastosFijos <- 50;
    resultado <- 1.21 * monto + gastosFijos;
FinProcedimiento

Procedimiento recibeArreglo(arreglo[] Como Entero Por Valor, arr2[] Como Entero Por Referencia)
    Escribir "Ejemplo de pasaje de arreglos.";
FinProcedimiento

Procedimiento agrupaParametros(uno, dos, tres Como Entero)
    Escribir "Varios parámetros en la misma definición. Las tres variables serán de tipo Entero y por valor.";
FinProcedimiento

Algoritmo ejemplo
    Definir monto, total Como Real;
    Escribir Sin Saltar "Ingrese monto: ";
    Leer monto;
    calcularTotal(total, monto);
    Escribir "El total es: ", total;    
FinAlgoritmo`.trim()

    listing[listing.length] = `
Funcion obtenerTotal(monto Como Real) Retorna total Como Real
    total <- 1.21 * monto + 50;
FinFuncion

Algoritmo ejemplo
    Definir monto, total Como Real;
    Escribir Sin Saltar "Ingrese monto: ";
    Leer monto;
    Escribir "El total con función es: ", obtenerTotal(monto);
FinAlgoritmo`.trim()

    listing[listing.length] = `
Definir precios[10] Como Entero;`.trim()

    listing[listing.length] = `
Algoritmo Primos
    // Código del algoritmo
FinAlgoritmo`.trim()

    listing[listing.length] = `
Algoritmo ejemplo
    Definir variable Como Entero;
    Segun variable Hacer
        1: Escribir "Uno";
        2: Escribir "Dos";
        De Otro Modo: Escribir "Mayor a dos";
    FinSegun
FinAlgoritmo`.trim()
    export default {
        name: 'Help',
        props: ['id'],
        components: {
            codemirror
        },
        data () {
            return {
                listing: listing,
                dateCompiled: process.env.VERSION_DATE,
                cmOption: {
                    mode: 'psflex',
                    tabSize: 4,
                    lineNumbers: true,
                    autoRefresh: true
                }
            }
        }
    }
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>

    .help {
        font-size: 16px;
        font-family: 'Roboto', sans-serif;
        padding: 12px;
        text-align: center;
        height: calc(100% - 24px);
        overflow-x: hidden;
        overflow-y: auto;
        background-color: #F8F8E8;
    }
    .help-container {
        display: inline-block;
        text-align: left;
        max-width: 800px;
        margin-bottom: 120px;

    }
    h1, h2 {
        color: #333;
    }

    p {
        color: #555;
    }
    .code-help {
        font-size: 12px;
    }

    .code-keyword {
        color: #708;
        font-family: "Inconsolata", monospace;
    }

    .code-list {
        font-size: 14px;
    }
</style>


// WEBPACK FOOTER //
// src/Components/Help/Help.vue